import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateCustomerEffortScoreSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const longText1Id = uuidv4();
  const longText1BlockId = uuidv4();
  const longText2Id = uuidv4();
  const longText2BlockId = uuidv4();
  const dropdownId = uuidv4();
  const dropdownBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const dropdownOption1Id = uuidv4();
  const dropdownOption2Id = uuidv4();
  const dropdownOption3Id = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const dropdownMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.espressoGoldenSand,
    "collectionId": collectionId,
    "name": "Customer Effort Score Survey Template",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "DETATCHED_RIGHT",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "How did we do?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'd love a quick check-in about your recent support experience.",
        "thirdContent": "Start",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "PEXEL",
          "id": welcomeMediaId,
          "type": "video",
          "name": "4452781",
          "link": "https://videos.pexels.com/video-files/4452781/4452781-hd_1280_720_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4452781/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "How easy was it to get the help you needed today?",
        "manditory": false,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 1,
        "maxRange": 10,
        "minLabel": "Very difficult",
        "maxLabel": "Very easy",
        "secondaryContent": "Keep it simple; your first impression tells us a lot.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText1Id,
        "blockId": longText1BlockId,
        "content": `What made you choose that score of {{${optionScaleBlockId}}}/10?`,
        "manditory": false,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Any extra details will help us improve the experience.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longText2Id,
        "blockId": longText2BlockId,
        "content": "Is there anything we could've done to make things smoother?",
        "manditory": false,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Be honest, we appreciate it.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "DROPDOWN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dropdownId,
        "blockId": dropdownBlockId,
        "content": "How long have you been using our service?",
        "manditory": false,
        "order": 4,
        "selectOptions": [
          {
            "id": dropdownOption1Id,
            "order": 0,
            "value": "I'm brand new"
          },
          {
            "id": dropdownOption2Id,
            "order": 1,
            "value": "1 to 3 years"
          },
          {
            "id": dropdownOption3Id,
            "order": 2,
            "value": "Over 3 years"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "PEXEL",
          "id": dropdownMediaId,
          "type": "video",
          "name": "4452790",
          "link": "https://videos.pexels.com/video-files/4452790/4452790-hd_1280_720_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4452790/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "Thanks! Your feedback helps us get better every day.",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Explore More",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 24,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "4452792",
          "link": "https://videos.pexels.com/video-files/4452792/4452792-hd_1280_720_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4452792/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      }
    ],
    "snippets": [],
    "metaTitle": "Customer Effort Score Survey Template",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateCustomerEffortScoreSurveyTemplate;